import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SeoHead from '../../components/base/SeoHead';

interface BlogPost {
  id: number;
  title: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  author: string;
  authorImage: string;
  content: string[];
}

export default function BlogDetailPage() {
  const { id } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const blogPosts: { [key: number]: BlogPost } = {
    1: {
      id: 1,
      title: '如何為寵物選擇安全的香氛產品',
      category: '寵物照顧',
      date: '2024-01-15',
      readTime: '5分鐘',
      image: 'https://public.readdy.ai/ai/img_res/54dcee000530a522b7dd8347d4e147d2.jpg',
      author: '林芳儀',
      authorImage: 'https://readdy.ai/api/search-image?query=professional%20asian%20female%20aromatherapy%20expert%20portrait%20with%20clean%20white%20background%2C%20friendly%20smile%2C%20natural%20style&width=200&height=200&seq=authordetail1&orientation=squarish',
      content: [
        '作為寵物家長，我們都希望為毛孩創造一個舒適的居家環境。香氛產品能夠改善空氣品質、營造溫馨氛圍，但並非所有香氛產品都適合有寵物的家庭。本文將教您如何選擇對寵物安全的香氛產品。',
        '首先，我們需要了解哪些成分對寵物有害。許多常見的精油，如茶樹油、尤加利油、肉桂油等，對貓狗都可能造成毒性反應。這些精油可能導致寵物出現嘔吐、腹瀉、呼吸困難等症狀。',
        '選擇寵物友善香氛產品的關鍵在於：1. 選擇經過寵物安全認證的產品；2. 避免含有有害精油成分；3. 選擇天然植物萃取的香氛；4. 注意香氛濃度不宜過高；5. 確保空間通風良好。',
        '薰衣草、洋甘菊、雪松等精油在適當稀釋後，對大多數寵物是相對安全的。但即使是安全的精油，也應該從低濃度開始使用，並觀察寵物的反應。如果寵物出現任何不適症狀，應立即停止使用並諮詢獸醫。',
        '在 MOFU 淨寵居，我們所有的香氛產品都經過嚴格的寵物安全測試，使用天然成分，確保您和毛孩都能安心享受香氛生活。我們也提供專業的諮詢服務，幫助您選擇最適合的產品。'
      ]
    },
    2: {
      id: 2,
      title: '香氛蠟燭製作入門指南',
      category: '香氛知識',
      date: '2024-01-12',
      readTime: '8分鐘',
      image: 'https://readdy.ai/api/search-image?query=beautiful%20handmade%20scented%20candles%20arrangement%20with%20natural%20ingredients%20on%20clean%20white%20surface%2C%20candle%20making%20tutorial%20scene%2C%20soft%20warm%20lighting%2C%20minimalist%20aesthetic%2C%20aromatherapy%20crafting%20guide%20visual&width=1200&height=600&seq=blogdetail2&orientation=landscape',
      author: '陳雅婷',
      authorImage: 'https://readdy.ai/api/search-image?query=professional%20asian%20female%20candle%20maker%20portrait%20with%20clean%20white%20background%2C%20warm%20smile%2C%20artistic%20style&width=200&height=200&seq=authordetail2&orientation=squarish',
      content: [
        '手作香氛蠟燭不僅能為居家空間增添溫馨氛圍，製作過程本身也是一種療癒的體驗。本文將帶您了解香氛蠟燭製作的基礎知識，讓您也能輕鬆入門這個美好的手作世界。',
        '製作香氛蠟燭需要準備的基本材料包括：蠟材（大豆蠟、蜂蠟或石蠟）、燭芯、香氛精油、容器、溫度計、攪拌棒等。對於初學者，建議從大豆蠟開始，因為它天然環保、燃燒時間長，而且容易操作。',
        '製作步驟：1. 將蠟材隔水加熱至適當溫度（約60-70°C）；2. 加入香氛精油，比例約為蠟材重量的6-10%；3. 充分攪拌均勻；4. 將燭芯固定在容器中央；5. 緩慢倒入蠟液；6. 靜置冷卻24小時。',
        '香氛調配是蠟燭製作的靈魂。初學者可以從單一香調開始，如純薰衣草或純柑橘。熟練後，可以嘗試調配複合香調，例如薰衣草+雪松、柑橘+薄荷等組合，創造獨特的香氛體驗。',
        '想要深入學習香氛蠟燭製作技巧嗎？歡迎參加 MOFU 淨寵居的香氛實驗室課程，我們的專業講師將手把手教您製作專屬的香氛蠟燭，並分享更多進階技巧。'
      ]
    }
  };

  const post = blogPosts[parseInt(id || '1')] || blogPosts[1];

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mofu.com.tw';

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "首頁", "item": siteUrl },
          { "@type": "ListItem", "position": 2, "name": "部落格", "item": `${siteUrl}/blog` },
          { "@type": "ListItem", "position": 3, "name": post.title, "item": `${siteUrl}/blog/${post.id}` }
        ]
      },
      {
        "@type": "Article",
        "@id": `${siteUrl}/blog/${post.id}#article`,
        "headline": post.title,
        "description": post.content[0].substring(0, 160),
        "image": {
          "@type": "ImageObject",
          "url": post.image,
          "width": 1200,
          "height": 600
        },
        "datePublished": post.date,
        "dateModified": post.date,
        "author": {
          "@type": "Person",
          "name": post.author,
          "image": post.authorImage
        },
        "publisher": {
          "@type": "Organization",
          "name": "MOFU 淨寵居",
          "url": siteUrl,
          "logo": {
            "@type": "ImageObject",
            "url": "https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/c0ff92e5a82252bc47aa78b3b10aa452.png"
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `${siteUrl}/blog/${post.id}`
        },
        "articleSection": post.category,
        "wordCount": post.content.join(' ').length,
        "timeRequired": post.readTime,
        "inLanguage": "zh-TW"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      <SeoHead
        title={`${post.title} | MOFU 淨寵居部落格`}
        description={post.content[0].substring(0, 155)}
        keywords={`${post.category},${post.title},寵物照顧,香氛知識,MOFU淨寵居`}
        ogImage={post.image}
        schema={schema}
      />

      {/* Hero Image */}
      <div className="relative h-96 overflow-hidden">
        <div className="w-full h-full">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
      </div>

      <div className="max-w-4xl mx-auto px-6 -mt-32 relative z-10">
        {/* Article Header */}
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl mb-12">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
            <Link to="/" className="hover:text-coral-500 cursor-pointer whitespace-nowrap">首頁</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/blog" className="hover:text-coral-500 cursor-pointer whitespace-nowrap">部落格</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-800">{post.title}</span>
          </div>

          <span className="inline-block px-4 py-2 bg-coral-100 text-coral-500 rounded-full text-sm font-medium mb-6 whitespace-nowrap">
            {post.category}
          </span>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            {post.title}
          </h1>

          <div className="flex items-center gap-6 pb-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full overflow-hidden">
                <img src={post.authorImage} alt={post.author} className="w-full h-full object-cover object-top" />
              </div>
              <div>
                <p className="font-medium text-gray-800">{post.author}</p>
                <p className="text-sm text-gray-500">香氛專家</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {post.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {post.readTime}
              </span>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm mb-12">
          <div className="prose prose-lg max-w-none">
            {post.content.map((paragraph, index) => (
              <p key={index} className="text-gray-700 leading-relaxed mb-6">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Share Buttons */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-4">分享文章</p>
            <div className="flex gap-3">
              <button className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-coral-500 hover:text-white transition-colors cursor-pointer">
                <i className="ri-facebook-fill"></i>
              </button>
              <button className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-coral-500 hover:text-white transition-colors cursor-pointer">
                <i className="ri-line-fill"></i>
              </button>
              <button className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-coral-500 hover:text-white transition-colors cursor-pointer">
                <i className="ri-twitter-fill"></i>
              </button>
              <button className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-coral-500 hover:text-white transition-colors cursor-pointer">
                <i className="ri-link"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Related Posts */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            相關文章
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.values(blogPosts).filter(p => p.id !== post.id).slice(0, 2).map((relatedPost) => (
              <Link
                key={relatedPost.id}
                to={`/blog/${relatedPost.id}`}
                className="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <div className="w-full h-full">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-coral-100 text-coral-500 rounded-full text-xs font-medium mb-3 whitespace-nowrap">
                    {relatedPost.category}
                  </span>
                  <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2 group-hover:text-coral-500 transition-colors">
                    {relatedPost.title}
                  </h3>
                  <p className="text-sm text-gray-500">{relatedPost.date}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}